import { css, StyleSheet } from 'aphrodite';
import { type FC, useCallback } from 'react';

import { useLocalStorageState } from '../../../hooks/useLocalStorage';
import { Button } from '../../../web/components-v2/Button';
import type { CoachMessage } from '../ai';
import type { CallState } from '../call';
import type { KeywordPin } from '../pins';
import { CallStrip } from './CallStrip';
import { IconPin } from './icons';
import { LatestCoachingHero, type LatestCoachingHeroMessage } from './LatestCoachingHero';
import { PinnedCard } from './PinnedCard';
import { StarterRow } from './StarterRow';

const PIN_HEIGHTS_KEY = 'focused-view:pin-heights';
const GAME_PLAN_HEIGHT_KEY = 'GAME PLAN';

/**
 * Identity used to persist a pinned card's height. Stable keyword pins
 * ("GAME PLAN" etc.) survive across sessions; ad-hoc pins fall back to the
 * message id and persist only within the current conversation. Mirrors the
 * key used previously in `RightColumn` so heights survive the layout swap.
 */
const heightKeyFor = (msg: CoachMessage): string => msg.pinnedKeyword || msg.id;

/**
 * Props for {@link CenterColumn}. All data is injected from the top-level
 * `FocusedLeadView` container; the column itself holds no server state.
 */
export type CenterColumnProps = {
	callState: CallState;
	muted: boolean;
	onAnswer: () => void;
	onReject: () => void;
	onHangup: () => void;
	onToggleMute: () => void;
	onEnableAudio: () => void;
	/**
	 * Send a DTMF digit on the connected call. Forwarded to CallStrip's
	 * inline dialpad popover. Optional so consumers (e.g. tests) can omit
	 * it; the dialpad toggle is hidden when this is not provided.
	 */
	onSendDigit?: (digit: string) => void;
	/**
	 * True while the Twilio SDK is trying to recover from a transient audio
	 * drop on an active call. Forwarded to CallStrip so the connected strip
	 * surfaces an amber "Reconnecting…" indicator until the next
	 * `call:reconnected` (or `call:ended`).
	 */
	reconnecting?: boolean;

	/**
	 * Preferred display for the active caller/callee on the dark CallStrip,
	 * e.g. `"Acme Corp · Patrick Walters"`. Forwarded as `callerDisplay` so
	 * the strip shows the lead identity instead of the raw E.164 number while
	 * dialing/connected/ringing. Falls back to the call's E.164 string when
	 * absent (e.g. before lead data has loaded).
	 */
	peerDisplay?: string;

	/**
	 * Display for the currently RINGING inbound caller, resolved from the
	 * server-side phone-call record + lead contacts. Takes precedence over
	 * `peerDisplay` while the strip is in `ringing` status — the rep needs
	 * to know who is calling them, which may not be the currently-loaded
	 * lead.
	 */
	inboundDisplay?: string;
	/**
	 * Optional click handler that opens the ringing caller's lead in the
	 * focused view without answering. Makes the caller name in the ringing
	 * strip a link so the rep can preview the lead before deciding.
	 */
	onOpenInboundLead?: () => void;

	/** Full chronological message stream. Oldest first — matches `useCoaching`. */
	messages: CoachMessage[];
	/** IDs the user has pinned to the rail. */
	pinnedIds: string[];
	onTogglePin: (id: string) => void;

	/** True while the AI is streaming a reply — drives the hero's streaming dot. */
	isResponding: boolean;

	/** Sticky-bottom conversation starters (parent filters to enabled `trigger='starter'`). */
	starters: KeywordPin[];
	onSendStarter: (pin: KeywordPin) => void;

	/**
	 * Realtime-coaching websocket connection state. When defined, a small
	 * green/red dot is rendered next to the hero eyebrow with a hover tooltip
	 * (see `LatestCoachingHero`). Omit when coaching is not active.
	 */
	wsConnected?: boolean;

	/**
	 * If true and the call is connected, the "Get suggestion" button is shown.
	 * Mirrors the legacy gating: visible only when the user has opted out of
	 * automatic suggestions (`coffeePreferences.autoSuggestionsEnabled === false`).
	 */
	showGetSuggestionButton?: boolean;
	onRequestSuggestion?: () => void;

	/**
	 * If true, the "Get summary" button is shown above the pinned-card stack.
	 * Set by the parent on `call:ended` and cleared once the user clicks the
	 * button or a new conversation begins.
	 */
	showGetSummaryButton?: boolean;
	onRequestSummary?: () => void;
};

/**
 * The 40%-wide middle column. Composes:
 *  1. `CallStrip` — call lifecycle UI at the top.
 *  2. `LatestCoachingHero` — the most recent assistant message rendered as
 *     a prominent hero card. Streams its dot indicator while `isResponding`.
 *  3. A sticky-top GAME PLAN card (when the auto-pinned Game Plan message is
 *     present and still pinned). Lives outside the scrollable stack so reps
 *     don't scroll past it once chatter accumulates.
 *  4. A scrollable pinned-card stack (lifted from `RightColumn`). The hero
 *     and stack intentionally duplicate when the latest assistant message is
 *     pinned — the hero is the live focus, the stack is a persistent anchor.
 *  5. A sticky-bottom row of conversation starters (`StarterRow` per starter).
 *     The footer is hidden entirely when no starters are configured.
 *
 * The chat thread, composer, "Sales Coach" head row, and "New conversation"
 * button have moved out of CENTER. The composer + thread land in RightColumn
 * (Task 2.3); the new-conversation control needs a new home in a follow-up.
 */
export const CenterColumn: FC<CenterColumnProps> = ({
	callState,
	muted,
	onAnswer,
	onReject,
	onHangup,
	onToggleMute,
	onEnableAudio,
	onSendDigit,
	reconnecting,
	peerDisplay,
	inboundDisplay,
	onOpenInboundLead,
	messages,
	pinnedIds,
	onTogglePin,
	isResponding,
	starters,
	onSendStarter,
	wsConnected,
	showGetSuggestionButton,
	onRequestSuggestion,
	showGetSummaryButton,
	onRequestSummary,
}) => {
	// findLast() is ES2023 and not supported in Firefox 91 ESR (in our browserslist).
	// Iterate from the end manually. Pinned assistant messages are skipped —
	// they already render in the dedicated Game Plan / pinned-stack slots
	// below, and showing the same message in the hero would duplicate it
	// verbatim (the auto-pin contract makes this trivially reachable for
	// e.g. the first reply, which becomes the Game Plan).
	let latest: CoachMessage | undefined;
	for (let i = messages.length - 1; i >= 0; i--) {
		const m = messages[i];
		if (m.role !== 'assistant') continue;
		if (pinnedIds.includes(m.id)) continue;
		latest = m;
		break;
	}
	// Adapt the wider `CoachMessage` to the hero's narrower message shape.
	// The loop above already guarantees the role is `'assistant'`; the explicit
	// projection keeps the hero contract honest without leaking transcript-
	// shaped messages through.
	const heroMessage: LatestCoachingHeroMessage | undefined = latest
		? {
				id: latest.id,
				role: 'assistant',
				text: latest.text,
				pinnedKeyword: latest.pinnedKeyword,
				pinColor: latest.pinColor,
				sayThis: latest.sayThis,
				why: latest.why,
			}
		: undefined;

	// Split the pinned messages into "the GAME PLAN card" and "everything else".
	// The Game Plan is auto-pinned by FocusedLeadView's keyword decoration; if
	// the user explicitly unpins it, it falls out of `pinnedMessages` entirely
	// and we fall back to normal stack inclusion. See I5 in the focused-view
	// release plan.
	const pinnedMessages = messages.filter(m => pinnedIds.includes(m.id));
	const gamePlanMessage = pinnedMessages.find(m => m.pinnedKeyword === 'GAME PLAN');
	const otherPinnedMessages = pinnedMessages.filter(m => m !== gamePlanMessage);

	const [pinHeights, setPinHeights] = useLocalStorageState<Record<string, number>>(PIN_HEIGHTS_KEY, {});
	const handleResize = useCallback(
		(key: string, height: number) => {
			setPinHeights(prev => {
				if (height <= 0) {
					if (!(key in prev)) return prev;
					const { [key]: _removed, ...rest } = prev;
					return rest;
				}
				if (prev[key] === height) return prev;
				return { ...prev, [key]: height };
			});
		},
		[setPinHeights]
	);

	const showSuggestionButton = Boolean(
		showGetSuggestionButton && onRequestSuggestion && callState.status === 'connected'
	);
	const showSummaryButton = Boolean(showGetSummaryButton && onRequestSummary);
	const showActionRow = showSuggestionButton || showSummaryButton;

	return (
		<div className={css(styles.col)}>
			<CallStrip
				callState={callState}
				muted={muted}
				onAnswer={onAnswer}
				onReject={onReject}
				onHangup={onHangup}
				onToggleMute={onToggleMute}
				onEnableAudio={onEnableAudio}
				callerDisplay={peerDisplay}
				inboundDisplay={inboundDisplay}
				onOpenInboundLead={onOpenInboundLead}
				onSendDigit={onSendDigit}
				reconnecting={reconnecting}
			/>

			<div className={css(styles.heroWrap)}>
				<LatestCoachingHero
					message={heroMessage}
					streaming={isResponding}
					pinned={latest ? pinnedIds.includes(latest.id) : false}
					onTogglePin={() => latest && onTogglePin(latest.id)}
					wsConnected={wsConnected}
				/>
			</div>

			{showActionRow && (
				<div className={css(styles.actionRow)}>
					{showSuggestionButton && (
						<Button
							variant='primary'
							size='sm'
							onClick={onRequestSuggestion}
							disabled={isResponding}
							data-testid='get-suggestion-button'
						>
							{isResponding ? 'Analyzing…' : 'Get suggestion'}
						</Button>
					)}
					{showSummaryButton && (
						<Button
							variant='outline'
							size='sm'
							onClick={onRequestSummary}
							disabled={isResponding}
							data-testid='get-summary-button'
						>
							{isResponding ? 'Generating summary…' : 'Get summary'}
						</Button>
					)}
				</div>
			)}

			{gamePlanMessage && (
				<div className={css(styles.gamePlanWrap)} data-testid='game-plan-card'>
					<PinnedCard
						msg={gamePlanMessage}
						onUnpin={() => onTogglePin(gamePlanMessage.id)}
						maxHeight={pinHeights[GAME_PLAN_HEIGHT_KEY]}
						onResize={h => handleResize(GAME_PLAN_HEIGHT_KEY, h)}
						elevated
					/>
				</div>
			)}

			<div className={css(styles.stackScroll)}>
				{otherPinnedMessages.length > 0 ? (
					<div className={css(styles.pinnedList)}>
						{otherPinnedMessages.map(m => {
							const key = heightKeyFor(m);
							return (
								<PinnedCard
									key={m.id}
									msg={m}
									onUnpin={() => onTogglePin(m.id)}
									maxHeight={pinHeights[key]}
									onResize={h => handleResize(key, h)}
								/>
							);
						})}
					</div>
				) : !gamePlanMessage ? (
					<EmptyPinned />
				) : null}
			</div>

			{starters.length > 0 && (
				<div className={css(styles.starterFooter)}>
					<div className={css(styles.starterHead)}>
						<span className={css(styles.starterLabel)}>Conversation starters · {starters.length}</span>
						<span className={css(styles.starterHint)}>tap to send</span>
					</div>
					<div className={css(styles.starterList)}>
						{starters.map(p => (
							<StarterRow key={p.id} pin={p} onSend={() => onSendStarter(p)} />
						))}
					</div>
				</div>
			)}
		</div>
	);
};

const EmptyPinned: FC = () => (
	<div className={css(styles.emptyCard)}>
		<div className={css(styles.emptyIcon)}>
			<IconPin size={14} />
		</div>
		<div className={css(styles.emptyText)}>
			Hover any AI message and click the pin icon to keep it here for the call.
		</div>
	</div>
);

const styles = StyleSheet.create({
	col: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		minHeight: 0,
		flex: 1,
		background: 'var(--bg)',
	},
	heroWrap: {
		padding: '16px 20px 0',
		flexShrink: 0,
		// Cap the hero height so a long streaming AI message doesn't eat the
		// whole viewport and starve the pinned-card stack of scroll room. The
		// inner hero card scrolls internally past this cap.
		maxHeight: '40vh',
		overflowY: 'auto',
		minHeight: 0,
	},
	actionRow: {
		display: 'flex',
		gap: 8,
		padding: '10px 20px 0',
		flexShrink: 0,
	},
	gamePlanWrap: {
		padding: '14px 20px 0',
		flexShrink: 0,
	},
	stackScroll: {
		flex: 1,
		overflowY: 'auto',
		minHeight: 0,
		padding: '16px 20px 24px',
	},
	pinnedList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
	},
	starterFooter: {
		flexShrink: 0,
		padding: '12px 16px 16px',
		borderTop: '1px solid var(--border)',
		background: 'var(--bg)',
	},
	starterHead: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 10,
	},
	starterLabel: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	starterHint: {
		fontSize: 10,
		color: 'var(--ink-faint)',
		fontFamily: 'var(--font-mono)',
	},
	starterList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	emptyCard: {
		padding: '20px 16px',
		border: '1px dashed var(--border)',
		borderRadius: 10,
		textAlign: 'center',
	},
	emptyIcon: {
		width: 32,
		height: 32,
		borderRadius: 8,
		background: 'var(--bg-elev)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: 'var(--ink-faint)',
		marginBottom: 8,
	},
	emptyText: {
		fontSize: 12.5,
		color: 'var(--ink-muted)',
		lineHeight: 1.5,
	},
});
